import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { bankReconApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Upload, FileText, AlertTriangle, CheckCircle2, XCircle, Trash2, RefreshCw,
  Search, Filter, TrendingUp, TrendingDown, PlusCircle, Wand2, Copy,
  Settings, ChevronUp, ChevronDown, ChevronRight, Pencil, Check, GripVertical, Tag, Download, Eye, ShieldCheck, History, FolderInput,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/utils';
import ActivityLogPanel from './ActivityLogPanel';

// ────────────────────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────────────────────
const fmt$ = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
};

// US-format date: MM/DD/YYYY.  Accepts ISO string or Date.
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const s = String(iso).split('T')[0];
    const [y, m, d] = s.split('-');
    if (y && m && d) return `${m}/${d}/${y}`;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return String(iso);
    return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
  } catch { return String(iso || ''); }
};

const CATEGORY_COLORS = {
  ach: 'bg-blue-100 text-blue-800',
  zelle: 'bg-violet-100 text-violet-800',
  wire: 'bg-indigo-100 text-indigo-800',
  check: 'bg-slate-100 text-slate-800',
  fuel: 'bg-amber-100 text-amber-800',
  insurance: 'bg-cyan-100 text-cyan-800',
  truck_payment: 'bg-orange-100 text-orange-800',
  maintenance: 'bg-yellow-100 text-yellow-800',
  payroll: 'bg-pink-100 text-pink-800',
  driver_payment: 'bg-pink-100 text-pink-800',
  oo_payment: 'bg-pink-100 text-pink-800',
  factoring: 'bg-emerald-100 text-emerald-800',
  broker_payment: 'bg-emerald-100 text-emerald-800',
  amazon_payment: 'bg-emerald-100 text-emerald-800',
  amazon_deposit: 'bg-emerald-100 text-emerald-800',
  bank_fee: 'bg-red-100 text-red-800',
  loan_payment: 'bg-red-100 text-red-800',
  tax_payment: 'bg-red-100 text-red-800',
  refund: 'bg-green-100 text-green-800',
  cash_withdrawal: 'bg-gray-100 text-gray-800',
  cash_deposit: 'bg-green-100 text-green-800',
  other: 'bg-slate-100 text-slate-800',
  uncategorized: 'bg-rose-100 text-rose-800',
};

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────
export default function BankReconciliation() {
  const [tab, setTab] = useState('transactions'); // transactions | import | rules

  // Manual transaction dialog
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: '',
    amount: '',
    txn_type: 'withdrawal',
    category: 'uncategorized',
    subcategory: '',
    notes: '',
    label: '',
    account_number: '',
  });
  const [manualSaving, setManualSaving] = useState(false);

  // Activity panel dialog
  const [activityOpen, setActivityOpen] = useState(false);

  // Catalogs
  const [categories, setCategories] = useState([]);
  const [facets, setFacets] = useState({ accounts: [], source_files: [], subcategories: [] });

  // Import review
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // {done, total, current}
  const [reviewBatches, setReviewBatches] = useState([]); // preview batches, not yet committed
  const [committedBatches, setCommittedBatches] = useState([]);

  // Transactions
  const [txns, setTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    category: '',
    subcategory: '',
    account_number: '',
    source_file: '',
    txn_type: '',
    reconciled: '',
    excluded: '',
    search: '',
    min_amount: '',
    max_amount: '',
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCat, setBulkCat] = useState('');
  const [bulkSub, setBulkSub] = useState('');
  // Sort spec for the txn table.  col: 'date' | 'amount' | 'description', dir: 'asc' | 'desc'.
  const [sort, setSort] = useState({ col: 'date', dir: 'desc' });

  // Rules
  const [rules, setRules] = useState({ builtin: [], user: [] });
  const [newRule, setNewRule] = useState({
    pattern: '',
    category: '',
    subcategory: '',
    apply_to_existing: true,
    amount_op: '',
    amount_value: '',
    exclude_on_match: false,
  });
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingRuleDraft, setEditingRuleDraft] = useState({ pattern: '', category: '', subcategory: '' });

  // Category management
  const [catMgrOpen, setCatMgrOpen] = useState(false);

  const fileInputRef = useRef(null);

  // ── Loaders ─────────────────────────────────────────────────────────────
  const loadCatalogs = useCallback(async () => {
    try {
      const [cats, fac, r] = await Promise.all([
        bankReconApi.listCategories(),
        bankReconApi.facets(),
        bankReconApi.listRules(),
      ]);
      setCategories(Array.isArray(cats.data?.categories) ? cats.data.categories : []);
      setFacets(fac.data || { accounts: [], source_files: [], subcategories: [] });
      setRules(r.data || { builtin: [], user: [] });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to load catalogs'));
    }
  }, []);

  const loadTxns = useCallback(async () => {
    setLoadingTxns(true);
    try {
      const q = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        // txn_type direction (in/out) is applied client-side in visibleTxns,
        // so don't send it to the backend which expects 'debit'/'credit'.
        if (k === 'txn_type') return;
        if (k === 'reconciled' || k === 'excluded') q[k] = v === 'true';
        else q[k] = v;
      });
      const [txRes, sumRes] = await Promise.all([
        bankReconApi.listTxns(q),
        bankReconApi.categorySummary({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          account_number: filters.account_number || undefined,
        }),
      ]);
      setTxns(txRes.data.txns);
      setSummary(sumRes.data);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to load transactions'));
    } finally {
      setLoadingTxns(false);
    }
  }, [filters]);

  const loadBatches = useCallback(async () => {
    try {
      const res = await bankReconApi.listBatches();
      const preview = res.data.filter((b) => b.status === 'preview');
      const committed = res.data.filter((b) => b.status !== 'preview');
      // preview batches don't include preview_rows on list endpoint — fetch each
      const withRows = await Promise.all(preview.map(async (b) => {
        const detail = await bankReconApi.deleteBatch // placeholder — actually we want GET
          ? null : null;
        return b;
      }));
      // fetch each preview batch's rows
      const detailed = await Promise.all(preview.map(async (b) => {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/batches/${b.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (!r.ok) return b;
          const detail = await r.json();
          return detail;
        } catch { return b; }
      }));
      setReviewBatches(detailed);
      setCommittedBatches(committed);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to load batches'));
    }
  }, []);

  useEffect(() => { loadCatalogs(); loadBatches(); }, [loadCatalogs, loadBatches]);
  // PERF: Debounce load on filter changes so rapid typing in the search box
  // doesn't fire an API call + heavy re-render on every keystroke.
  useEffect(() => {
    const h = setTimeout(() => { loadTxns(); }, 300);
    return () => clearTimeout(h);
  }, [loadTxns]);

  // ── Upload ─────────────────────────────────────────────────────────────
  const handleFileChoose = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length, current: files[0]?.name });
    try {
      // 1. Kick off the async job — returns { job_id, status: 'running' } fast
      const start = await bankReconApi.uploadFiles(files);
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error('Upload did not return a job id');

      // 2. Poll every 2 s until done. Give up after 5 min to avoid hung UI.
      const started = Date.now();
      const MAX_WAIT_MS = 5 * 60 * 1000;
      let finalPayload = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - started > MAX_WAIT_MS) {
          throw new Error('Upload timed out after 5 minutes. Try a smaller file.');
        }
        await new Promise((r) => setTimeout(r, 2000));
        let statusRes;
        try {
          statusRes = await bankReconApi.uploadStatus(jobId);
        } catch (err) {
          // A single transient poll failure shouldn't abort the whole flow —
          // give it another cycle to recover.
          continue;
        }
        const d = statusRes.data || {};
        if (d.progress) setUploadProgress(d.progress);
        if (d.status === 'done') { finalPayload = d.result; break; }
        if (d.status === 'failed') {
          throw new Error(d.error || 'Upload failed');
        }
      }

      const batches = finalPayload?.batches || [];
      toast.success(`${batches.length} file(s) uploaded — review below`);
      setTab('import');
      await loadBatches();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const commitBatch = async (batch, excludeRowIds) => {
    try {
      const res = await bankReconApi.commit([{ batch_id: batch.id, exclude_row_ids: excludeRowIds, bank_account_override: batch.bank_account || null }]);
      toast.success(`Imported ${res.data.imported} transactions`);
      await loadBatches();
      await loadTxns();
      await loadCatalogs();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Commit failed'));
    }
  };

  const cancelBatch = async (batchId) => {
    if (!window.confirm('Discard this import batch? No transactions will be created.')) return;
    try {
      await bankReconApi.deleteBatch(batchId);
      toast.success('Batch cancelled');
      await loadBatches();
    } catch (e) { toast.error(apiErrorMessage(e, 'Cancel failed')); }
  };

  const revertBatch = async (batchId) => {
    if (!window.confirm('Revert this committed batch? All its transactions will be soft-deleted.')) return;
    try {
      await bankReconApi.deleteBatch(batchId);
      toast.success('Batch reverted');
      await loadBatches();
      await loadTxns();
    } catch (e) { toast.error(apiErrorMessage(e, 'Revert failed')); }
  };

  // ── Bulk actions ───────────────────────────────────────────────────────
  const runBulk = async (op, value, subcategory) => {
    if (selectedIds.size === 0) return toast.error('Select at least one transaction');
    try {
      const res = await bankReconApi.bulkTxns({
        ids: Array.from(selectedIds), op,
        value: value || null,
        subcategory: subcategory ?? null,
      });
      toast.success(`Updated ${res.data.modified} transaction(s)`);
      setBulkCat(''); setBulkSub('');
      await loadTxns();
    } catch (e) { toast.error(apiErrorMessage(e, 'Bulk action failed')); }
  };

  // PERF: debounce summary refresh — patchOne can be called rapidly (edit
  // category on many rows) and each call triggered a heavy aggregation.
  const summaryRefreshTimer = useRef(null);
  const scheduleSummaryRefresh = useCallback(() => {
    if (summaryRefreshTimer.current) clearTimeout(summaryRefreshTimer.current);
    summaryRefreshTimer.current = setTimeout(async () => {
      try {
        const sumRes = await bankReconApi.categorySummary({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          account_number: filters.account_number || undefined,
        });
        setSummary(sumRes.data);
      } catch { /* ignore */ }
    }, 500);
  }, [filters.start_date, filters.end_date, filters.account_number]);

  const patchOne = async (id, patch) => {
    try {
      // Optimistic update FIRST so the UI is instant
      setTxns((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
      await bankReconApi.patchTxn(id, patch);
      // Debounced summary refresh
      scheduleSummaryRefresh();
    } catch (e) { toast.error(apiErrorMessage(e, 'Update failed')); }
  };

  // ── Rules ──────────────────────────────────────────────────────────────
  const addRule = async () => {
    if (!newRule.pattern || !newRule.category) return toast.error('Pattern and category are required');
    try {
      const payload = {
        pattern: newRule.pattern,
        category: newRule.category,
        subcategory: newRule.subcategory || null,
        apply_to_existing: newRule.apply_to_existing,
        amount_op: newRule.amount_op || null,
        amount_value: newRule.amount_value ? parseFloat(newRule.amount_value) : null,
        exclude_on_match: newRule.exclude_on_match,
      };
      const res = await bankReconApi.createRule(payload);
      toast.success(`Rule saved${res.data.applied_to_existing ? ` — retagged ${res.data.applied_to_existing} txns` : ''}`);
      setNewRule({
        pattern: '', category: '', subcategory: '', apply_to_existing: true,
        amount_op: '', amount_value: '', exclude_on_match: false,
      });
      await loadCatalogs();
      await loadTxns();
    } catch (e) { toast.error(apiErrorMessage(e, 'Rule create failed')); }
  };

  const patchRule = async (id, patch) => {
    try {
      await bankReconApi.patchRule(id, patch);
      await loadCatalogs();
      toast.success('Rule updated');
    } catch (e) { toast.error(apiErrorMessage(e, 'Update failed')); }
  };

  const reapplyAllRules = async () => {
    try {
      const res = await bankReconApi.reapplyAllRules();
      toast.success(`Reapplied — recategorized ${res.data.updated} transactions`);
      await loadTxns();
    } catch (e) { toast.error(apiErrorMessage(e, 'Reapply failed')); }
  };

  const deleteRule = async (id) => {
    try { await bankReconApi.deleteRule(id); await loadCatalogs(); toast.success('Rule deleted'); }
    catch (e) { toast.error(apiErrorMessage(e, 'Delete failed')); }
  };

  // PDF preview modal state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewLabel, setPdfPreviewLabel] = useState('');

  const buildStatementParams = () => {
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (!params.start_date || !params.end_date) {
      if (txns.length === 0) return null;
      const dates = txns.map((t) => t.date).filter(Boolean).sort();
      params.start_date = dates[0];
      params.end_date = dates[dates.length - 1];
    }
    if (filters.account_number) params.account_number = filters.account_number;
    if (filters.category) params.category = filters.category;
    if (filters.subcategory) params.subcategory = filters.subcategory;
    if (filters.search) params.search = filters.search;
    if (filters.txn_type) params.txn_type = filters.txn_type;
    return params;
  };

  const previewStatementPdf = async () => {
    const params = buildStatementParams();
    if (!params) return toast.error('Load or filter to a date range first');
    setPdfPreviewLoading(true);
    setPdfPreviewOpen(true);
    const label = filters.category
      ? (categoryNameById[filters.category] || filters.category)
      : 'All categories';
    setPdfPreviewLabel(`${label} · ${params.start_date} → ${params.end_date}`);
    try {
      const res = await bankReconApi.statementPdf(params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      // clean up any previous blob url
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error(apiErrorMessage(e, 'PDF preview failed'));
      setPdfPreviewOpen(false);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  // Run deduction check across the full ledger
  const [runningDedCheck, setRunningDedCheck] = useState(false);
  const [dedCheckResult, setDedCheckResult] = useState(null); // {total_scanned, matched, cleared, matches:[...]}
  const [dedResultOpen, setDedResultOpen] = useState(false);
  const runDeductionCheck = async () => {
    setRunningDedCheck(true);
    try {
      // Scope to whatever is currently visible in the txn view.  If no filter
      // is set, the backend scans year-to-date across every unit.
      const scope = { window_days: 7 };
      if (filters.category) scope.category = filters.category;
      if (filters.subcategory) scope.subcategory = filters.subcategory;
      if (filters.account_number) scope.account_number = filters.account_number;
      if (filters.start_date) scope.start_date = filters.start_date;
      if (filters.end_date) scope.end_date = filters.end_date;
      if (filters.txn_type) scope.txn_type = filters.txn_type;
      const res = await bankReconApi.runDeductionCheck(scope);
      setDedCheckResult(res.data);
      const total = (res.data.matches || []).length;
      const { matched, cleared } = res.data;
      const scopeLabel = filters.category
        ? `in ${categoryNameById[filters.category] || filters.category}`
        : 'year-to-date';
      const bits = [`Scanned ${res.data.total_scanned} ${scopeLabel}`];
      if (matched) bits.push(`${matched} newly matched`);
      if (cleared) bits.push(`${cleared} cleared`);
      if (!matched && !cleared) bits.push('no changes');
      if (total > 0) {
        setDedResultOpen(true);
        toast.success(`${bits.join(' · ')} — showing ${total} match${total === 1 ? '' : 'es'}`);
      } else {
        toast.info(`${bits.join(' · ')} — no matches found`);
      }
      await loadTxns();
    } catch (e) { toast.error(apiErrorMessage(e, 'Deduction check failed')); }
    finally { setRunningDedCheck(false); }
  };

  // Actions from the deduction results dialog
  const dedMoveCategory = async (txnId, categoryId) => {
    try {
      await bankReconApi.patchTxn(txnId, { category: categoryId });
      toast.success('Category updated');
      setTxns((prev) => prev.map((t) => (t.id === txnId ? { ...t, category: categoryId } : t)));
      setDedCheckResult((prev) => prev && {
        ...prev,
        matches: prev.matches.map((m) => m.txn_id === txnId ? { ...m, _acted: 'moved', _acted_to: categoryId } : m),
      });
    } catch (e) { toast.error(apiErrorMessage(e, 'Update failed')); }
  };

  const dedDelete = async (txnId) => {
    if (!window.confirm('Delete this transaction? This soft-deletes it — it can be restored from the Import History.')) return;
    try {
      await bankReconApi.bulkTxns({ ids: [txnId], op: 'delete' });
      toast.success('Transaction deleted');
      setTxns((prev) => prev.filter((t) => t.id !== txnId));
      setDedCheckResult((prev) => prev && {
        ...prev,
        matches: prev.matches.map((m) => m.txn_id === txnId ? { ...m, _acted: 'deleted' } : m),
      });
    } catch (e) { toast.error(apiErrorMessage(e, 'Delete failed')); }
  };

  const dedDismiss = (txnId) => {
    setDedCheckResult((prev) => prev && {
      ...prev,
      matches: prev.matches.map((m) => m.txn_id === txnId ? { ...m, _acted: 'dismissed' } : m),
    });
  };

  const downloadStatementPdf = async () => {
    try {
      const params = buildStatementParams();
      if (!params) return toast.error('Load or filter to a date range first');
      const res = await bankReconApi.statementPdf(params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = filters.category || 'all';
      a.download = `bank-recon-${slug}-${params.start_date}-to-${params.end_date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Statement PDF downloaded');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'PDF export failed'));
    }
  };

  // ── Derived / UI helpers ───────────────────────────────────────────────
  const allSelected = useMemo(() => txns.length > 0 && txns.every((t) => selectedIds.has(t.id)), [txns, selectedIds]);
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(txns.map((t) => t.id)));
  };
  const toggleOne = (id) => {
    const n = new Set(selectedIds);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelectedIds(n);
  };

  const categoryNameById = useMemo(() => {
    const m = {};
    (categories || []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [categories]);

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="bank-recon-root">
      {/* Tab bar — wraps and adds inline actions.  On mobile the Upload
          Statements + Manage Categories buttons fall to a second row. */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        {['transactions', 'import', 'rules'].map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-2 transition-colors whitespace-nowrap ${
              tab === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`bank-recon-tab-${k}`}
          >
            {k === 'transactions' ? 'Transactions' : k === 'import' ? `Import Review${reviewBatches.length ? ` (${reviewBatches.length})` : ''}` : 'Rules'}
          </button>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.csv,.xlsx,.xls"
            multiple
            onChange={handleFileChoose}
            className="hidden"
            data-testid="bank-recon-file-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCatMgrOpen(true)}
            data-testid="manage-categories-btn"
          >
            <Settings className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Manage Categories</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setManualDialogOpen(true)}
            title="Manually add a transaction (missing from statement or accidentally deleted)"
            data-testid="add-manual-txn-btn"
          >
            <PlusCircle className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Add Manual</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActivityOpen(true)}
            title="View recent transaction activity"
            data-testid="bank-recon-activity-btn"
          >
            <History className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Activity</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={runDeductionCheck}
            disabled={runningDedCheck || txns.length === 0}
            title="Scan all transactions for matches against existing settlement deductions"
            data-testid="run-deduction-check-btn"
          >
            <ShieldCheck className={`h-4 w-4 sm:mr-1.5 ${runningDedCheck ? 'animate-pulse' : ''}`} />
            <span className="hidden sm:inline">{runningDedCheck ? 'Checking…' : 'Deduction Check'}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={previewStatementPdf}
            disabled={txns.length === 0}
            title="Preview the Bank Reconciliation Statement PDF"
            data-testid="preview-statement-pdf-btn"
          >
            <Eye className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Preview PDF</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadStatementPdf}
            disabled={txns.length === 0}
            title={txns.length === 0 ? 'Import a statement first' : 'Download Bank Reconciliation Statement PDF'}
            data-testid="download-statement-pdf-btn"
          >
            <Download className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Statement PDF</span>
          </Button>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="bank-recon-upload-btn"
          >
            <Upload className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">
              {uploading
                ? (uploadProgress
                    ? `Uploading ${uploadProgress.done ?? 0} of ${uploadProgress.total ?? '?'}…`
                    : 'Uploading…')
                : 'Upload Statements'}
            </span>
            <span className="sm:hidden">{uploading ? '…' : 'Upload'}</span>
          </Button>
        </div>
      </div>

      {tab === 'transactions' && (
        <TransactionsView
          txns={txns}
          loading={loadingTxns}
          summary={summary}
          filters={filters}
          setFilters={setFilters}
          categories={categories}
          categoryNameById={categoryNameById}
          facets={facets}
          selectedIds={selectedIds}
          toggleAll={toggleAll}
          toggleOne={toggleOne}
          allSelected={allSelected}
          patchOne={patchOne}
          runBulk={runBulk}
          bulkCat={bulkCat}
          setBulkCat={setBulkCat}
          bulkSub={bulkSub}
          setBulkSub={setBulkSub}
          sort={sort}
          setSort={setSort}
        />
      )}

      {tab === 'import' && (
        <ImportReview
          reviewBatches={reviewBatches}
          committedBatches={committedBatches}
          commitBatch={commitBatch}
          cancelBatch={cancelBatch}
          revertBatch={revertBatch}
          categories={categories}
          categoryNameById={categoryNameById}
        />
      )}

      {tab === 'rules' && (
        <RulesView
          rules={rules}
          categories={categories}
          categoryNameById={categoryNameById}
          newRule={newRule}
          setNewRule={setNewRule}
          addRule={addRule}
          patchRule={patchRule}
          deleteRule={deleteRule}
          reapplyAllRules={reapplyAllRules}
          editingRuleId={editingRuleId}
          setEditingRuleId={setEditingRuleId}
          editingRuleDraft={editingRuleDraft}
          setEditingRuleDraft={setEditingRuleDraft}
        />
      )}

      <CategoryManagerDialog
        open={catMgrOpen}
        onOpenChange={setCatMgrOpen}
        categories={categories}
        onChanged={loadCatalogs}
      />

      {/* Deduction Check results dialog — shows every txn matched to a
          settlement deduction with per-row actions */}
      <Dialog open={dedResultOpen} onOpenChange={setDedResultOpen}>
        <DialogContent className="sm:max-w-[820px] w-[92vw] max-h-[85vh] flex flex-col p-0" data-testid="ded-check-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-600" /> Deduction Check Results
            </DialogTitle>
            <DialogDescription className="text-xs">
              {dedCheckResult?.scope?.category || dedCheckResult?.scope?.subcategory || dedCheckResult?.scope?.txn_type ? (
                <>
                  Scanned <strong>{dedCheckResult?.total_scanned ?? 0}</strong> transactions in the current filter
                  {dedCheckResult?.scope?.category && <> · category <strong>{categoryNameById[dedCheckResult.scope.category] || dedCheckResult.scope.category}</strong></>}
                  {dedCheckResult?.scope?.subcategory && <> · subcategory <strong>{dedCheckResult.scope.subcategory}</strong></>}
                  {dedCheckResult?.scope?.txn_type && <> · <strong>{dedCheckResult.scope.txn_type === 'in' ? 'Money In' : 'Money Out'}</strong> only</>}
                  {'. '}
                </>
              ) : (
                <>Scanned <strong>{dedCheckResult?.total_scanned ?? 0}</strong> transactions year-to-date across every unit. </>
              )}
              Found <strong>{(dedCheckResult?.matches || []).length}</strong> that match an existing settlement deduction
              within ±{dedCheckResult?.window_days ?? 7} days on the exact amount.
              For each match, choose to <strong>move to a different category</strong>, <strong>delete</strong> the transaction,
              or <strong>leave</strong> it flagged.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
            {(dedCheckResult?.matches || []).length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-8 italic">
                No matches — nothing to review.
              </div>
            )}
            {(dedCheckResult?.matches || []).map((m) => (
              <div
                key={m.txn_id}
                className={`rounded border p-3 space-y-2 ${
                  m._acted === 'deleted' ? 'opacity-40 line-through border-red-300 bg-red-50/50 dark:bg-red-950/20' :
                  m._acted === 'dismissed' ? 'opacity-50 border-slate-300' :
                  m._acted === 'moved' ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20' :
                  'border-amber-300 bg-amber-50/40 dark:bg-amber-950/20'
                }`}
                data-testid={`ded-match-row-${m.txn_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  {/* Left: transaction */}
                  <div className="flex-1 min-w-[240px]">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Transaction</div>
                    <div className="text-sm font-medium truncate">{m.txn_description}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDate(m.txn_date)} · <span className={m.txn_amount < 0 ? 'text-red-700' : 'text-emerald-700'}>{fmt$(m.txn_amount)}</span>
                    </div>
                  </div>
                  {/* Right: deduction */}
                  <div className="flex-1 min-w-[240px] bg-white/60 dark:bg-slate-900/40 rounded p-2 border">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Matched deduction</div>
                    <div className="text-sm font-medium truncate">{m.deduction.name || m.deduction.type || 'Deduction'}</div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      <div>
                        Week of <strong>{fmtDate(m.deduction.week_start)}</strong>
                        {' · '}<span className="text-red-700">{fmt$(m.deduction.amount || 0)}</span>
                      </div>
                      {m.deduction.unit?.unit_number && (
                        <div data-testid={`ded-match-unit-${m.txn_id}`}>
                          Unit <strong>#{m.deduction.unit.unit_number}</strong>
                          {m.deduction.unit.make_model && <span className="text-[10px] opacity-70"> · {m.deduction.unit.make_model}</span>}
                        </div>
                      )}
                      {m.deduction.driver?.name && (
                        <div data-testid={`ded-match-driver-${m.txn_id}`}>Driver <strong>{m.deduction.driver.name}</strong></div>
                      )}
                      <div className="text-[10px] opacity-60">Source: {m.deduction.source}</div>
                    </div>
                  </div>
                </div>
                {/* Actions */}
                {!m._acted && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
                    <span className="text-[11px] text-muted-foreground">Change category:</span>
                    <Select onValueChange={(v) => dedMoveCategory(m.txn_id, v)}>
                      <SelectTrigger className="h-7 w-44 text-xs" data-testid={`ded-move-select-${m.txn_id}`}>
                        <SelectValue placeholder="Move to…" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => dedDelete(m.txn_id)} data-testid={`ded-delete-${m.txn_id}`}>
                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={() => dedDismiss(m.txn_id)} data-testid={`ded-leave-${m.txn_id}`}>
                      Leave
                    </Button>
                  </div>
                )}
                {m._acted === 'moved' && <div className="text-xs text-emerald-700 font-medium">✓ Moved to {categoryNameById[m._acted_to] || m._acted_to}</div>}
                {m._acted === 'deleted' && <div className="text-xs text-red-700 font-medium">✓ Deleted</div>}
                {m._acted === 'dismissed' && <div className="text-xs text-slate-600 font-medium">Left flagged</div>}
              </div>
            ))}
          </div>
          <DialogFooter className="p-3 border-t">
            <Button variant="outline" onClick={() => setDedResultOpen(false)} data-testid="ded-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF preview dialog — inline preview of the Statement PDF */}
      <Dialog open={pdfPreviewOpen} onOpenChange={(o) => { if (!o) closePdfPreview(); }}>
        <DialogContent className="sm:max-w-[900px] w-[92vw] h-[88vh] p-0 flex flex-col" data-testid="pdf-preview-dialog">
          <DialogHeader className="p-4 pr-12 pb-2 flex-row items-center justify-between space-y-0 border-b">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" /> Statement Preview
              </DialogTitle>
              <DialogDescription className="text-xs mt-0.5">{pdfPreviewLabel}</DialogDescription>
            </div>
            <div className="flex items-center gap-2 mr-4">
              {pdfPreviewUrl && (
                <Button
                  size="sm"
                  onClick={() => { downloadStatementPdf(); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="pdf-preview-download-btn"
                >
                  <Download className="h-4 w-4 mr-1.5" /> Download
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100 dark:bg-slate-950">
            {pdfPreviewLoading && (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Generating preview…
              </div>
            )}
            {!pdfPreviewLoading && pdfPreviewUrl && (
              <iframe
                src={pdfPreviewUrl}
                title="Statement PDF preview"
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Transaction Dialog — user-created entry for missing/deleted rows */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="sm:max-w-[560px] w-[92vw] p-0" data-testid="manual-txn-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4 text-blue-600" /> Add Manual Transaction
            </DialogTitle>
            <DialogDescription className="text-xs">
              Add a missing or accidentally-deleted transaction. Negative amount = money out.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Date</Label>
                <Input type="date" value={manualForm.date}
                       onChange={(e) => setManualForm((s) => ({ ...s, date: e.target.value }))}
                       data-testid="manual-txn-date" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Amount ($) — negative = out</Label>
                <Input type="number" step="0.01" placeholder="-42.50" value={manualForm.amount}
                       onChange={(e) => {
                         const val = e.target.value;
                         setManualForm((s) => ({
                           ...s,
                           amount: val,
                           txn_type: parseFloat(val) > 0 ? 'deposit' : 'withdrawal',
                         }));
                       }}
                       data-testid="manual-txn-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Description</Label>
              <Input placeholder="Fuel · Pilot #123" value={manualForm.description}
                     onChange={(e) => setManualForm((s) => ({ ...s, description: e.target.value }))}
                     data-testid="manual-txn-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Type</Label>
                <Select value={manualForm.txn_type}
                        onValueChange={(v) => setManualForm((s) => ({ ...s, txn_type: v }))}>
                  <SelectTrigger className="h-9" data-testid="manual-txn-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="withdrawal">Withdrawal (out)</SelectItem>
                    <SelectItem value="deposit">Deposit (in)</SelectItem>
                    <SelectItem value="fee">Fee</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Category</Label>
                <Select value={manualForm.category}
                        onValueChange={(v) => setManualForm((s) => ({ ...s, category: v, subcategory: '' }))}>
                  <SelectTrigger className="h-9" data-testid="manual-txn-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Subcategory (optional)</Label>
                <Input placeholder="—" value={manualForm.subcategory}
                       onChange={(e) => setManualForm((s) => ({ ...s, subcategory: e.target.value }))}
                       data-testid="manual-txn-subcategory" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Custom label (optional)</Label>
                <Input placeholder="Truck #12" value={manualForm.label}
                       onChange={(e) => setManualForm((s) => ({ ...s, label: e.target.value }))}
                       data-testid="manual-txn-label" />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Notes (optional)</Label>
              <Textarea rows={2} placeholder="Restored — deleted by mistake"
                        value={manualForm.notes}
                        onChange={(e) => setManualForm((s) => ({ ...s, notes: e.target.value }))}
                        data-testid="manual-txn-notes" />
            </div>
          </div>
          <DialogFooter className="p-3 border-t gap-2">
            <Button variant="outline" onClick={() => setManualDialogOpen(false)} data-testid="manual-txn-cancel">Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={manualSaving || !manualForm.description || !manualForm.amount}
              onClick={async () => {
                const amt = parseFloat(manualForm.amount);
                if (Number.isNaN(amt) || amt === 0) return toast.error('Amount cannot be 0');
                setManualSaving(true);
                try {
                  await bankReconApi.createManualTxn({
                    date: manualForm.date,
                    description: manualForm.description.trim(),
                    amount: amt,
                    txn_type: manualForm.txn_type,
                    category: manualForm.category,
                    subcategory: manualForm.subcategory || null,
                    label: manualForm.label || null,
                    notes: manualForm.notes || null,
                    account_number: manualForm.account_number || null,
                  });
                  toast.success('Transaction added');
                  setManualDialogOpen(false);
                  setManualForm((s) => ({ ...s, description: '', amount: '', notes: '', label: '', subcategory: '' }));
                  loadTxns();
                } catch (e) {
                  toast.error(apiErrorMessage(e, 'Failed to add'));
                } finally {
                  setManualSaving(false);
                }
              }}
              data-testid="manual-txn-save"
            >
              {manualSaving ? 'Saving…' : 'Add Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Reconciliation Activity Panel */}
      <Dialog open={activityOpen} onOpenChange={setActivityOpen}>
        <DialogContent className="sm:max-w-[720px] w-[94vw] max-h-[80vh] p-0 flex flex-col" data-testid="bank-recon-activity-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-blue-600" /> Bank Reconciliation Activity
            </DialogTitle>
            <DialogDescription className="text-xs">
              Recent transactions added, deleted, edited, categorized or bulk-moved.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <ActivityLogPanel variant="compact" source="bank_recon" showHeader={false} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Category manager dialog — add / rename / delete / reorder
// ────────────────────────────────────────────────────────────────────────────
function CategoryManagerDialog({ open, onOpenChange, categories, onChanged }) {
  const [items, setItems] = useState(categories);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('expense');
  const [newColor, setNewColor] = useState('#3b82f6');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}
  const [migrateTo, setMigrateTo] = useState('uncategorized');
  const [saving, setSaving] = useState(false);
  const [expandedCatId, setExpandedCatId] = useState(null); // which category shows its subs
  const [subsByCat, setSubsByCat] = useState({}); // { catId: [{id,name,category_id}] }

  useEffect(() => { if (open) setItems(categories); }, [open, categories]);

  const loadSubs = useCallback(async (catId) => {
    try {
      const r = await bankReconApi.listSubcategories(catId);
      setSubsByCat((prev) => ({ ...prev, [catId]: r.data.subcategories || [] }));
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (expandedCatId && !subsByCat[expandedCatId]) loadSubs(expandedCatId);
  }, [expandedCatId, subsByCat, loadSubs]);

  const [moveUnderFor, setMoveUnderFor] = useState(null); // {id, name} or null

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = async (event) => {
    // Drag-and-drop now ONLY reorders top-level categories — no more fuzzy
    // "did it land in the middle of another row?" nesting. Users nest via the
    // explicit "Move under…" button on each row, which opens a picker dialog.
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((c) => c.id === active.id);
    const newIdx = items.findIndex((c) => c.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    try {
      await bankReconApi.reorderCategories(next.map((c) => c.id));
      onChanged();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Reorder failed'));
      setItems(items);   // rollback
    }
  };

  // Explicit nest → open the picker for the source category
  const openMoveUnder = (src) => {
    if (!src) return;
    if (src.id === 'uncategorized') {
      return toast.error("'Uncategorized' cannot be nested");
    }
    setMoveUnderFor({ id: src.id, name: src.name });
  };

  const confirmMoveUnder = async (targetCat) => {
    if (!moveUnderFor || !targetCat) return;
    if (targetCat.id === moveUnderFor.id) return;
    try {
      await bankReconApi.convertToSubcategory(moveUnderFor.id, targetCat.id);
      toast.success(`"${moveUnderFor.name}" is now a subcategory of "${targetCat.name}"`);
      setMoveUnderFor(null);
      onChanged();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Nesting failed'));
    }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return toast.error('Name is required');
    setSaving(true);
    try {
      await bankReconApi.createCategory({ name, type: newType, color: newColor });
      setNewName('');
      setNewColor('#3b82f6');
      toast.success('Category added');
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, 'Add failed')); }
    finally { setSaving(false); }
  };

  const saveColor = async (id, color) => {
    try {
      await bankReconApi.patchCategory(id, { color });
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, 'Color update failed')); }
  };

  const saveRename = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      await bankReconApi.patchCategory(id, { name });
      setEditingId(null);
      onChanged();
      toast.success('Renamed');
    } catch (e) { toast.error(apiErrorMessage(e, 'Rename failed')); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await bankReconApi.deleteCategory(deleteTarget.id, migrateTo);
      toast.success(`Deleted — ${res.data.moved_txns} transactions moved to ${migrateTo}`);
      setDeleteTarget(null);
      setMigrateTo('uncategorized');
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, 'Delete failed')); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] flex flex-col" data-testid="cat-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-4 w-4" /> Manage Categories</DialogTitle>
            <DialogDescription>
              Drag categories to reorder. Click the pencil to rename, trash to delete (transactions get moved to another bucket).
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-1 border rounded p-2">
            <p className="text-[10px] text-muted-foreground px-1 pb-1 border-b mb-1 leading-relaxed">
              <strong>Drag between rows</strong> to reorder. <strong>Drag directly onto a row</strong> (over its center)
              to make one category a subcategory of another.
            </p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={items.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                {items.map((c) => (
                  <React.Fragment key={c.id}>
                    <SortableCategoryRow
                      cat={c}
                      isEditing={editingId === c.id}
                      editingName={editingName}
                      setEditingName={setEditingName}
                      onStartEdit={() => { setEditingId(c.id); setEditingName(c.name); }}
                      onCancelEdit={() => setEditingId(null)}
                      onSaveRename={() => saveRename(c.id)}
                      onDelete={() => setDeleteTarget({ id: c.id, name: c.name })}
                      onColorChange={(color) => saveColor(c.id, color)}
                      onMoveUnder={() => openMoveUnder(c)}
                      isExpanded={expandedCatId === c.id}
                      onToggleExpand={() => setExpandedCatId(expandedCatId === c.id ? null : c.id)}
                      subCount={(subsByCat[c.id] || []).length}
                    />
                    {expandedCatId === c.id && (
                      <SubcategoryEditor
                        categoryId={c.id}
                        subs={subsByCat[c.id] || []}
                        reload={() => loadSubs(c.id)}
                      />
                    )}
                  </React.Fragment>
                ))}
              </SortableContext>
            </DndContext>
          </div>

          <div className="border rounded p-3 space-y-2 bg-slate-50 dark:bg-slate-900/40">
            <p className="text-xs font-medium">Add a new category</p>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <Input placeholder="e.g. Tolls, Permits" value={newName} onChange={(e) => setNewName(e.target.value)}
                     className="flex-1" data-testid="new-cat-name" />
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger className="sm:w-32" data-testid="new-cat-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <label
                className="relative inline-flex items-center justify-center h-10 w-10 rounded border cursor-pointer shrink-0"
                style={{ backgroundColor: newColor }}
                title="Pick a color"
                data-testid="new-cat-color-swatch"
              >
                <input
                  type="color"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  data-testid="new-cat-color"
                />
              </label>
              <Button
                onClick={add}
                disabled={saving || !newName.trim()}
                data-testid="new-cat-save"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <PlusCircle className="h-4 w-4 mr-1.5" /> Add
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</DialogTitle>
            <DialogDescription>
              Any transactions currently in this category will be moved to another one you choose.
              The original description on every transaction is preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Move transactions to</Label>
            <Select value={migrateTo} onValueChange={setMigrateTo}>
              <SelectTrigger data-testid="cat-migrate-to"><SelectValue /></SelectTrigger>
              <SelectContent>
                {items.filter((c) => c.id !== deleteTarget?.id).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              data-testid="confirm-delete-cat"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move-under picker — replaces the flaky drag-to-nest heuristic. */}
      <Dialog open={!!moveUnderFor} onOpenChange={(o) => !o && setMoveUnderFor(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="move-under-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <FolderInput className="h-4 w-4" /> Move &ldquo;{moveUnderFor?.name}&rdquo; under…
            </DialogTitle>
            <DialogDescription>
              Pick the parent category to nest under. All existing transactions and rules
              will be re-pointed automatically. This is reversible — the subcategory can
              be promoted back to a top-level category later.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[320px] overflow-y-auto rounded border divide-y">
            {items
              .filter((c) => c.id !== moveUnderFor?.id && c.id !== 'uncategorized')
              .map((c) => (
                <button
                  key={c.id}
                  onClick={() => confirmMoveUnder(c)}
                  className="w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
                  data-testid={`move-under-pick-${c.id}`}
                >
                  <span
                    className="inline-block h-3.5 w-3.5 rounded border shrink-0"
                    style={{ backgroundColor: c.color || '#64748b' }}
                  />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] text-muted-foreground">{c.type}</span>
                </button>
              ))}
            {items.filter((c) => c.id !== moveUnderFor?.id && c.id !== 'uncategorized').length === 0 && (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Create another top-level category first to nest under it.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveUnderFor(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// One draggable row inside the category manager
function SortableCategoryRow({ cat, isEditing, editingName, setEditingName, onStartEdit, onCancelEdit, onSaveRename, onDelete, onColorChange, onMoveUnder, isExpanded, onToggleExpand, subCount }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const currentColor = cat.color || '#64748b';
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 group py-1 px-1 rounded ${isDragging ? 'bg-blue-50 dark:bg-blue-950/30 shadow' : ''}`}
      data-testid={`cat-mgr-row-${cat.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground p-1 -ml-1"
        aria-label="Drag to reorder"
        data-testid={`cat-drag-${cat.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <button
        onClick={onToggleExpand}
        className="p-0.5 text-muted-foreground hover:text-foreground"
        title={isExpanded ? 'Hide subcategories' : 'Show / manage subcategories'}
        data-testid={`cat-expand-mgr-${cat.id}`}
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      <label
        className="relative inline-block h-5 w-5 rounded border shrink-0 cursor-pointer"
        style={{ backgroundColor: currentColor }}
        title="Change color"
        data-testid={`cat-color-swatch-${cat.id}`}
      >
        <input
          type="color"
          value={currentColor}
          onChange={(e) => onColorChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
          data-testid={`cat-color-input-${cat.id}`}
        />
      </label>
      {isEditing ? (
        <>
          <Input value={editingName} autoFocus onChange={(e) => setEditingName(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && onSaveRename()}
                 className="h-8 flex-1" data-testid={`cat-rename-input-${cat.id}`} />
          <Button size="icon" variant="ghost" onClick={onSaveRename} className="h-8 w-8" data-testid={`cat-rename-save-${cat.id}`}>
            <Check className="h-4 w-4 text-emerald-600" />
          </Button>
          <Button size="icon" variant="ghost" onClick={onCancelEdit} className="h-8 w-8">
            <XCircle className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm truncate">{cat.name}</span>
          {subCount > 0 && (
            <span className="text-[10px] text-blue-700 bg-blue-50 dark:bg-blue-950/30 px-1.5 py-0.5 rounded" data-testid={`cat-sub-count-${cat.id}`}>
              {subCount} sub
            </span>
          )}
          <span className="text-[10px] text-muted-foreground shrink-0 mr-1">{cat.is_builtin ? 'built-in' : 'custom'}</span>
          <Button size="icon" variant="ghost" onClick={onStartEdit}
                  className="h-8 w-8 opacity-60 group-hover:opacity-100"
                  data-testid={`cat-edit-${cat.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={onMoveUnder}
            disabled={cat.id === 'uncategorized'}
            className="h-8 w-8 opacity-60 group-hover:opacity-100 text-blue-600"
            title={cat.id === 'uncategorized' ? "'Uncategorized' cannot be nested" : "Move under another category…"}
            data-testid={`cat-move-under-${cat.id}`}
          >
            <FolderInput className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" onClick={onDelete}
                  className="h-8 w-8 text-red-600 opacity-60 group-hover:opacity-100"
                  disabled={cat.id === 'uncategorized'}
                  title={cat.id === 'uncategorized' ? "'Uncategorized' is reserved and cannot be deleted" : 'Delete'}
                  data-testid={`cat-delete-${cat.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}

// Nested subcategory editor — visible when a category is expanded
function SubcategoryEditor({ categoryId, subs, reload }) {
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    const nm = name.trim();
    if (!nm) return;
    setSaving(true);
    try {
      await bankReconApi.createSubcategory({ category_id: categoryId, name: nm });
      setName('');
      toast.success('Subcategory added');
      reload();
    } catch (e) { toast.error(apiErrorMessage(e, 'Add failed')); }
    finally { setSaving(false); }
  };

  const saveRename = async (id) => {
    if (!editingName.trim()) return;
    try {
      await bankReconApi.patchSubcategory(id, { name: editingName.trim() });
      setEditingId(null);
      reload();
      toast.success('Renamed');
    } catch (e) { toast.error(apiErrorMessage(e, 'Rename failed')); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this subcategory? Transactions currently tagged with it will lose the tag.')) return;
    try {
      await bankReconApi.deleteSubcategory(id);
      toast.success('Subcategory deleted');
      reload();
    } catch (e) { toast.error(apiErrorMessage(e, 'Delete failed')); }
  };

  return (
    <div className="ml-9 pl-3 border-l-2 border-blue-200 dark:border-blue-800 py-1 space-y-1" data-testid={`sub-editor-${categoryId}`}>
      {subs.length === 0 && (
        <div className="text-[11px] text-muted-foreground italic px-1 py-0.5">No subcategories yet — add one below.</div>
      )}
      {subs.map((s) => (
        <div key={s.id} className="flex items-center gap-1.5 text-xs" data-testid={`sub-row-${s.id}`}>
          <span className="text-slate-400">↳</span>
          {editingId === s.id ? (
            <>
              <Input
                value={editingName}
                autoFocus
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveRename(s.id)}
                className="h-7 flex-1 text-xs"
                data-testid={`sub-rename-input-${s.id}`}
              />
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveRename(s.id)} data-testid={`sub-rename-save-${s.id}`}>
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <>
              <span className="flex-1 truncate">{s.name}</span>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setEditingId(s.id); setEditingName(s.name); }} data-testid={`sub-edit-${s.id}`}>
                <Pencil className="h-3 w-3" />
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => remove(s.id)} data-testid={`sub-delete-${s.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      ))}
      <div className="flex items-center gap-1.5 pt-1">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="e.g. Pilot, Love's, TA"
          className="h-7 flex-1 text-xs"
          data-testid={`sub-name-input-${categoryId}`}
        />
        <Button
          size="sm"
          onClick={add}
          disabled={saving || !name.trim()}
          className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
          data-testid={`sub-add-btn-${categoryId}`}
        >
          <PlusCircle className="h-3 w-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Expandable category list (sidebar) — shows parent totals; subcategories
// appear indented when the parent is expanded.
// ────────────────────────────────────────────────────────────────────────────
function CategoryList({ rows, categoryNameById, activeCategory, activeSubcategory, onToggleCat, onToggleSub }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  // Auto-expand a category that has an active subcategory
  useEffect(() => {
    if (activeCategory && activeSubcategory) {
      setExpanded((p) => ({ ...p, [activeCategory]: true }));
    }
  }, [activeCategory, activeSubcategory]);

  return (
    <>
      {rows.map((c) => {
        const subs = c.subcategories || [];
        const isOpen = expanded[c.category];
        const isActiveCat = activeCategory === c.category && !activeSubcategory;
        return (
          <div key={c.category} data-testid={`cat-filter-group-${c.category}`}>
            <div
              className={`flex items-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                isActiveCat ? 'bg-blue-100 dark:bg-blue-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {subs.length > 0 ? (
                <button
                  onClick={() => toggle(c.category)}
                  className="p-0.5 -ml-1 text-muted-foreground hover:text-foreground"
                  aria-label={isOpen ? 'Collapse' : 'Expand'}
                  data-testid={`cat-expand-${c.category}`}
                >
                  {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
              ) : (
                <span className="w-4" />
              )}
              <button
                onClick={() => onToggleCat(c.category)}
                className="flex-1 text-left flex items-center justify-between min-w-0"
                data-testid={`cat-filter-${c.category}`}
              >
                <span className="truncate mr-2">{categoryNameById[c.category] || c.category}</span>
                <span className="flex items-center gap-1 tabular-nums shrink-0">
                  <span className={`${c.total < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmt$(c.total)}</span>
                  <span className="text-muted-foreground">({c.count})</span>
                </span>
              </button>
            </div>
            {isOpen && subs.length > 0 && (
              <div className="ml-4 pl-2 border-l border-slate-200 dark:border-slate-700 space-y-0.5 mt-0.5 mb-1">
                {subs.map((s) => {
                  const isActiveSub = activeCategory === c.category && activeSubcategory === s.subcategory;
                  return (
                    <button
                      key={s.subcategory}
                      onClick={() => onToggleSub(c.category, s.subcategory)}
                      className={`w-full text-left px-2 py-1 rounded text-[11px] flex items-center justify-between transition-colors ${
                        isActiveSub ? 'bg-blue-100 dark:bg-blue-900/30 font-medium' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                      data-testid={`subcat-filter-${c.category}-${s.subcategory}`}
                    >
                      <span className="truncate mr-2 text-slate-700 dark:text-slate-300">↳ {s.subcategory}</span>
                      <span className="flex items-center gap-1 tabular-nums shrink-0">
                        <span className={s.total < 0 ? 'text-red-700' : 'text-emerald-700'}>{fmt$(s.total)}</span>
                        <span className="text-muted-foreground">({s.count})</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Small helpers used by TransactionsView
// ────────────────────────────────────────────────────────────────────────────
function SortableHeader({ label, col, sort, setSort, testId, align = 'left' }) {
  const active = sort?.col === col;
  const dir = active ? sort.dir : null;
  const onClick = () => {
    if (active) setSort({ col, dir: dir === 'asc' ? 'desc' : 'asc' });
    else setSort({ col, dir: col === 'amount' || col === 'date' ? 'desc' : 'asc' });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 hover:text-blue-700 dark:hover:text-blue-400 font-medium ${align === 'right' ? 'w-full justify-end' : ''}`}
      data-testid={testId}
    >
      {label}
      <span className="text-[10px]">
        {active ? (dir === 'asc' ? '▲' : '▼') : <span className="opacity-30">↕</span>}
      </span>
    </button>
  );
}

function BulkSubcategoryPicker({ categoryId, value, onChange }) {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    if (!categoryId) { setSubs([]); return; }
    (async () => {
      try {
        const r = await bankReconApi.listSubcategories(categoryId);
        setSubs(r.data.subcategories || []);
      } catch { setSubs([]); }
    })();
  }, [categoryId]);
  if (!categoryId) return null;
  return (
    <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
      <SelectTrigger className="h-7 w-40 text-xs" data-testid="bulk-subcategory-picker">
        <SelectValue placeholder="…and subcategory" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— No subcategory —</SelectItem>
        {subs.map((s) => (<SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>))}
      </SelectContent>
    </Select>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Transactions view (summary + filters + table + bulk actions)
// ────────────────────────────────────────────────────────────────────────────
function TransactionsView({
  txns, loading, summary, filters, setFilters, categories, categoryNameById, facets,
  selectedIds, toggleAll, toggleOne, allSelected, patchOne, runBulk, bulkCat, setBulkCat,
  bulkSub, setBulkSub, sort, setSort,
}) {
  // Local text/amount search + sort, computed client-side over the loaded txns.
  const visibleTxns = useMemo(() => {
    let arr = txns;
    const dir = filters.txn_type;
    if (dir === 'in') arr = arr.filter((t) => Number(t.amount) > 0);
    else if (dir === 'out') arr = arr.filter((t) => Number(t.amount) < 0);

    const sc = sort?.col;
    if (sc) {
      const asc = sort.dir === 'asc';
      const cmp = (a, b) => {
        let av, bv;
        if (sc === 'amount') { av = Number(a.amount) || 0; bv = Number(b.amount) || 0; }
        else if (sc === 'date') { av = a.date || ''; bv = b.date || ''; }
        else { av = (a.merchant || a.description || '').toLowerCase();
               bv = (b.merchant || b.description || '').toLowerCase(); }
        if (av < bv) return asc ? -1 : 1;
        if (av > bv) return asc ? 1 : -1;
        return 0;
      };
      arr = [...arr].sort(cmp);
    }
    return arr;
  }, [txns, filters.txn_type, sort]);

  const visibleTotals = useMemo(() => {
    let inSum = 0, outSum = 0;
    for (const t of visibleTxns) {
      const amt = Number(t.amount) || 0;
      if (amt > 0) inSum += amt;
      else outSum += -amt;
    }
    return { moneyIn: inSum, moneyOut: outSum, net: inSum - outSum };
  }, [visibleTxns]);

  // ─── PERF: Client-side pagination.  Rendering 1000+ table rows (each with
  // its own Radix Select) locks the browser for many seconds.  We render only
  // a page at a time (default 150 rows) and expose a "Show more" button.  When
  // the user filters/sorts, we reset back to the first page. ────────────────
  const PAGE = 150;
  const [visiblePage, setVisiblePage] = useState(1);
  useEffect(() => { setVisiblePage(1); }, [filters, sort, txns.length]);
  const pagedTxns = useMemo(
    () => visibleTxns.slice(0, visiblePage * PAGE),
    [visibleTxns, visiblePage],
  );
  const hasMore = pagedTxns.length < visibleTxns.length;

  // ─── PERF: only render the heavy Select for the row whose category is
  // currently being edited.  Every other row shows a lightweight coloured
  // badge that opens the editor on click.  This alone drops the DOM node
  // count from ~34 000 (row×select-item combos) to ~150 (visible rows).
  const [catEditRowId, setCatEditRowId] = useState(null);

  // Category lookup by id (name + color) for fast per-row rendering.
  const catById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c;
    return m;
  }, [categories]);

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      {/* Summary sidebar */}
      <div className="space-y-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Cash Flow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-emerald-700"><TrendingUp className="h-3.5 w-3.5" /> Money In</span>
              <strong className="tabular-nums">{fmt$(summary?.money_in ?? 0)}</strong>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-red-700"><TrendingDown className="h-3.5 w-3.5" /> Money Out</span>
              <strong className="tabular-nums">{fmt$(summary?.money_out ?? 0)}</strong>
            </div>
            <div className="flex items-center justify-between text-sm border-t pt-2">
              <span className="font-medium">Net</span>
              <strong className={`tabular-nums ${(summary?.net_cash_flow ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {fmt$(summary?.net_cash_flow ?? 0)}
              </strong>
            </div>
            <div className="text-xs text-muted-foreground pt-1 border-t space-y-1">
              <div className="flex justify-between">
                <span>Reconciled</span>
                <span>{fmt$(summary?.reconciled_total ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>Uncategorized</span>
                <span>{fmt$(summary?.uncategorized_total ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>Excluded</span>
                <span>{fmt$(summary?.excluded_total ?? 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">By Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-[420px] overflow-y-auto">
            <CategoryList
              rows={summary?.by_category || []}
              categoryNameById={categoryNameById}
              activeCategory={filters.category}
              activeSubcategory={filters.subcategory}
              onToggleCat={(cat) => setFilters((prev) => ({
                ...prev,
                category: prev.category === cat ? '' : cat,
                subcategory: '',
              }))}
              onToggleSub={(cat, sub) => setFilters((prev) => ({
                ...prev,
                category: cat,
                subcategory: prev.subcategory === sub ? '' : sub,
              }))}
            />
            {(!summary?.by_category || summary.by_category.length === 0) && (
              <div className="text-xs text-muted-foreground italic px-2 py-4 text-center">No transactions in this filter</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main table + filters */}
      <div className="space-y-3 order-1 lg:order-2">
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              <div className="col-span-2 md:col-span-3 lg:col-span-2">
                <Label className="text-xs">Search (text or amount)</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    className="pl-7 h-8 text-sm"
                    placeholder="description, reference, notes, or amount (e.g. 250)"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    data-testid="filter-search"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Start</Label>
                <Input type="date" className="h-8 text-sm" value={filters.start_date}
                       onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                       data-testid="filter-start-date" />
              </div>
              <div>
                <Label className="text-xs">End</Label>
                <Input type="date" className="h-8 text-sm" value={filters.end_date}
                       onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                       data-testid="filter-end-date" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={filters.category || 'all'} onValueChange={(v) => setFilters({ ...filters, category: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-8 text-sm" data-testid="filter-category"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Account</Label>
                <Select value={filters.account_number || 'all'} onValueChange={(v) => setFilters({ ...filters, account_number: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All accounts</SelectItem>
                    {facets.accounts.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Money direction quick chips */}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => setFilters({ ...filters, txn_type: '' })}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${!filters.txn_type ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'}`}
                data-testid="direction-all"
              >All</button>
              <button
                onClick={() => setFilters({ ...filters, txn_type: 'in' })}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors flex items-center gap-1 ${filters.txn_type === 'in' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-300 hover:border-emerald-500 dark:bg-slate-800 dark:text-emerald-300 dark:border-emerald-800'}`}
                data-testid="direction-in"
              ><TrendingUp className="h-3 w-3" /> Money In</button>
              <button
                onClick={() => setFilters({ ...filters, txn_type: 'out' })}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors flex items-center gap-1 ${filters.txn_type === 'out' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-300 hover:border-red-500 dark:bg-slate-800 dark:text-red-300 dark:border-red-800'}`}
                data-testid="direction-out"
              ><TrendingDown className="h-3 w-3" /> Money Out</button>
            </div>

            {selectedIds.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-blue-300 bg-blue-50/70 dark:bg-blue-950/20 p-2">
                <Badge variant="outline">{selectedIds.size} selected</Badge>
                <Select value={bulkCat} onValueChange={setBulkCat}>
                  <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="Change category…" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
                <BulkSubcategoryPicker
                  categoryId={bulkCat}
                  value={bulkSub}
                  onChange={setBulkSub}
                />
                <Button size="sm" variant="outline" onClick={() => bulkCat && runBulk('set_cat_and_sub', bulkCat, bulkSub || null)}
                        disabled={!bulkCat} data-testid="bulk-apply-category"
                        className="bg-blue-600 text-white hover:bg-blue-700 border-blue-600">Apply</Button>
                <Button size="sm" variant="outline" onClick={() => runBulk('mark_reconciled')}
                        data-testid="bulk-reconcile">Mark Reconciled</Button>
                <Button size="sm" variant="outline" onClick={() => runBulk('exclude')}
                        data-testid="bulk-exclude">Exclude</Button>
                <Button size="sm" variant="outline" onClick={() => runBulk('include')}
                        data-testid="bulk-include">Include</Button>
                <Button size="sm" variant="destructive" onClick={() => runBulk('delete')}
                        data-testid="bulk-delete">Delete</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Running totals bar — updates to reflect current filter/sort */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-1" data-testid="visible-totals-bar">
          <div className="text-xs text-muted-foreground">
            <strong className="text-slate-900 dark:text-slate-100 tabular-nums">{visibleTxns.length}</strong>
            {' '}visible / <span className="tabular-nums">{txns.length}</span> total
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> <span className="tabular-nums text-emerald-700 dark:text-emerald-400" data-testid="visible-money-in">+{fmt$(visibleTotals.moneyIn)}</span></span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> <span className="tabular-nums text-red-700 dark:text-red-400" data-testid="visible-money-out">-{fmt$(visibleTotals.moneyOut)}</span></span>
            <span className="border-l pl-3 flex items-center gap-1">
              <span className="text-muted-foreground">Net</span>
              <strong className={`tabular-nums ${visibleTotals.net >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`} data-testid="visible-net">
                {fmt$(visibleTotals.net)}
              </strong>
            </span>
          </div>
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" />
                  </TableHead>
                  <TableHead className="w-24">
                    <SortableHeader label="Date" col="date" sort={sort} setSort={setSort} testId="sort-date" />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Description" col="description" sort={sort} setSort={setSort} testId="sort-description" />
                  </TableHead>
                  <TableHead className="w-40">Category</TableHead>
                  <TableHead className="w-28 text-right">
                    <SortableHeader label="Amount" col="amount" sort={sort} setSort={setSort} testId="sort-amount" align="right" />
                  </TableHead>
                  <TableHead className="w-32">Account</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">Loading…</TableCell></TableRow>)}
                {!loading && visibleTxns.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">
                    No transactions. Upload a statement to get started.
                  </TableCell></TableRow>
                )}
                {!loading && pagedTxns.map((t) => (
                  <TableRow key={t.id} className={t.excluded ? 'opacity-50' : ''} data-testid={`txn-row-${t.id}`}>
                    <TableCell>
                      <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleOne(t.id)}
                             data-testid={`select-txn-${t.id}`} />
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap" data-testid={`txn-date-${t.id}`}>{fmtDate(t.date)}</TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{t.merchant || t.description}</div>
                      {t.merchant && t.merchant !== t.description && (
                        <div className="text-xs text-muted-foreground truncate max-w-[420px]">{t.description}</div>
                      )}
                      {t.reference_number && (<div className="text-[10px] text-muted-foreground">Ref {t.reference_number}</div>)}
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {t.already_deducted && (
                          <Badge variant="outline" className="border-amber-500 bg-amber-50 text-amber-800 text-[10px] px-1.5 py-0"
                                 title={t.deduction_match ? `Matches ${t.deduction_match.source} on ${t.deduction_match.week_start}` : ''}
                                 data-testid={`already-deducted-badge-${t.id}`}>
                            Already Deducted
                          </Badge>
                        )}
                        <LabelInline
                          value={t.label || ''}
                          onSave={(v) => patchOne(t.id, { label: v || null })}
                          testId={`label-${t.id}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {catEditRowId === t.id ? (
                        <Select
                          value={t.category || 'uncategorized'}
                          onValueChange={(v) => { patchOne(t.id, { category: v }); setCatEditRowId(null); }}
                          open
                          onOpenChange={(o) => { if (!o) setCatEditRowId(null); }}
                        >
                          <SelectTrigger className="h-7 text-xs" data-testid={`cat-select-${t.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                        </Select>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCatEditRowId(t.id)}
                          className="min-h-[36px] w-full px-2 py-1 rounded text-xs border text-center hover:ring-2 hover:ring-blue-400 transition flex flex-col items-center justify-center leading-tight"
                          style={{
                            backgroundColor: (catById[t.category]?.color || '#e2e8f0') + '22',
                            borderColor: catById[t.category]?.color || '#cbd5e1',
                            color: catById[t.category]?.color || '#334155',
                          }}
                          title="Click to change category"
                          data-testid={`cat-badge-${t.id}`}
                        >
                          {t.subcategory ? (
                            <>
                              <span className="font-semibold truncate max-w-full leading-tight">{t.subcategory}</span>
                              <span className="opacity-60 text-[9px] truncate max-w-full leading-tight mt-0.5">
                                in {catById[t.category]?.name || t.category || 'Uncategorized'}
                              </span>
                            </>
                          ) : (
                            <span className="font-medium truncate max-w-full leading-tight">
                              {catById[t.category]?.name || t.category || 'Uncategorized'}
                            </span>
                          )}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums text-sm font-medium ${t.amount < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      {fmt$(t.amount)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-[140px]" title={t.source_file}>
                      {t.account_number && <div>{t.account_number}</div>}
                      <div className="text-[10px]">{t.source_file}</div>
                    </TableCell>
                    <TableCell>
                      <input type="checkbox" checked={!!t.reconciled} onChange={(e) => patchOne(t.id, { reconciled: e.target.checked })}
                             title="Reconciled?" data-testid={`reconcile-${t.id}`} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* PERF: paginated view — show more button */}
        {hasMore && (
          <div className="flex items-center justify-center py-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVisiblePage((p) => p + 1)}
              data-testid="show-more-txns-btn"
            >
              Show {Math.min(PAGE, visibleTxns.length - pagedTxns.length)} more · <span className="text-muted-foreground ml-1">({pagedTxns.length} of {visibleTxns.length})</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Import review view
// ────────────────────────────────────────────────────────────────────────────
function ImportReview({ reviewBatches, committedBatches, commitBatch, cancelBatch, revertBatch, categories, categoryNameById }) {
  return (
    <div className="space-y-4">
      {reviewBatches.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No batches waiting for review. Click <strong>Upload Statements</strong> above to add PDFs, CSVs, or Excel files.
          </CardContent>
        </Card>
      )}

      {reviewBatches.map((b) => (<BatchReviewCard key={b.id} batch={b} commitBatch={commitBatch} cancelBatch={cancelBatch} categoryNameById={categoryNameById} />))}

      {committedBatches.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Import History</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {committedBatches.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm border-b py-1.5 last:border-0" data-testid={`committed-batch-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{b.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {b.parser_used} · {b.rows_parsed} parsed · {b.committed_count ?? '-'} imported · {b.statement_start} → {b.statement_end}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge
                    variant="outline"
                    className={b.status === 'committed'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : b.status === 'reverted'
                        ? 'border-slate-400 bg-slate-100 text-slate-700'
                        : 'border-blue-400 bg-blue-50 text-blue-800'}
                  >
                    {b.status}
                  </Badge>
                  {b.status === 'committed' && (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => revertBatch(b.id)}
                            data-testid={`revert-batch-${b.id}`}>Revert</Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BatchReviewCard({ batch, commitBatch, cancelBatch, categoryNameById }) {
  const [excludedRows, setExcludedRows] = useState(new Set(
    (batch.preview_rows || []).filter((r) => !r.included).map((r) => r.row_id)
  ));
  // "Show duplicates only" filter — helps the user review flagged rows quickly
  // instead of scrolling through a 200-row statement
  const [dupOnly, setDupOnly] = useState(false);

  const toggleRow = (row_id) => {
    const n = new Set(excludedRows);
    n.has(row_id) ? n.delete(row_id) : n.add(row_id);
    setExcludedRows(n);
  };

  const rows = batch.preview_rows || [];
  const visibleRows = dupOnly ? rows.filter((r) => r.is_duplicate) : rows;
  const dupCount = rows.filter((r) => r.is_duplicate).length;
  const willImport = rows.filter((r) => !excludedRows.has(r.row_id)).length;
  const totalIn = rows.filter((r) => !excludedRows.has(r.row_id) && r.amount > 0).reduce((s, r) => s + r.amount, 0);
  const totalOut = rows.filter((r) => !excludedRows.has(r.row_id) && r.amount < 0).reduce((s, r) => s + r.amount, 0);

  // Bulk actions scoped to the currently visible slice — makes "Include all
  // duplicates" / "Exclude all duplicates" one click when the dup filter is on.
  const bulkToggle = (include) => {
    setExcludedRows((prev) => {
      const n = new Set(prev);
      visibleRows.forEach((r) => {
        if (include) n.delete(r.row_id);
        else n.add(r.row_id);
      });
      return n;
    });
  };

  return (
    <Card data-testid={`review-batch-${batch.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {batch.filename}
              <Badge variant="outline" className="text-xs">{batch.parser_used}</Badge>
            </CardTitle>
            <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span>Bank: <strong>{batch.bank_name || 'unknown'}</strong></span>
              <span>Account: <strong>{batch.bank_account || '—'}</strong></span>
              <span>Period: <strong>{batch.statement_start || '—'} → {batch.statement_end || '—'}</strong></span>
              <span>Beg bal: <strong>{batch.beginning_balance != null ? fmt$(batch.beginning_balance) : '—'}</strong></span>
              <span>End bal: <strong>{batch.ending_balance != null ? fmt$(batch.ending_balance) : '—'}</strong></span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Parsed <strong>{batch.rows_parsed}</strong> · Skipped <strong>{batch.rows_skipped}</strong>{' · '}
              <span className={batch.duplicate_count > 0 ? 'text-blue-700 font-medium' : ''}>Duplicates {batch.duplicate_count}</span>
            </div>
            {/* Duplicate filter + bulk actions — appear only when there are dupes */}
            {dupCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`batch-dup-toolbar-${batch.id}`}>
                <Button
                  size="sm"
                  variant={dupOnly ? 'default' : 'outline'}
                  className={dupOnly ? 'bg-blue-600 hover:bg-blue-700 h-7 text-xs' : 'border-blue-300 text-blue-700 hover:bg-blue-50 h-7 text-xs'}
                  onClick={() => setDupOnly((v) => !v)}
                  data-testid={`batch-dup-filter-${batch.id}`}
                >
                  {dupOnly ? `Showing ${dupCount} duplicate${dupCount === 1 ? '' : 's'} — click to see all` : `Show duplicates only (${dupCount})`}
                </Button>
                {dupOnly && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => bulkToggle(true)}
                            data-testid={`batch-dup-include-all-${batch.id}`}>
                      Include all
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => bulkToggle(false)}
                            data-testid={`batch-dup-exclude-all-${batch.id}`}>
                      Exclude all
                    </Button>
                  </>
                )}
              </div>
            )}
            {batch.parse_warnings && batch.parse_warnings.length > 0 && (
              <details className="text-xs text-blue-700 mt-1">
                <summary className="cursor-pointer">Parser warnings ({batch.parse_warnings.length})</summary>
                <ul className="list-disc pl-5">{batch.parse_warnings.map((w, i) => (<li key={i}>{w}</li>))}</ul>
              </details>
            )}
            {batch.parse_errors && batch.parse_errors.length > 0 && (
              <div className="text-xs text-blue-800 mt-1">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {batch.parse_errors.join('; ')}
              </div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={() => cancelBatch(batch.id)}
                    data-testid={`cancel-batch-${batch.id}`}>Cancel</Button>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => commitBatch(batch, Array.from(excludedRows))}
              data-testid={`commit-batch-${batch.id}`}
            >
              Approve {willImport} txns ({fmt$(totalIn)} in / {fmt$(totalOut)} out)
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Include</TableHead>
              <TableHead className="w-24">Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-40">Category</TableHead>
              <TableHead className="w-28 text-right">Amount</TableHead>
              <TableHead className="w-24">Flag</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((r) => (
              <TableRow key={r.row_id} className={excludedRows.has(r.row_id) ? 'opacity-40 line-through' : (r.is_duplicate ? 'bg-blue-50/40' : '')}>
                <TableCell>
                  <input type="checkbox" checked={!excludedRows.has(r.row_id)} onChange={() => toggleRow(r.row_id)}
                         data-testid={`preview-include-${r.row_id}`} />
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                <TableCell className="text-sm">
                  <div>{r.merchant || r.description}</div>
                  {r.reference_number && (<div className="text-[10px] text-muted-foreground">Ref {r.reference_number}</div>)}
                </TableCell>
                <TableCell className="text-xs">
                  <span className={`px-1.5 py-0.5 rounded ${CATEGORY_COLORS[r.category] || CATEGORY_COLORS.uncategorized}`}>
                    {categoryNameById[r.category] || r.category}
                  </span>
                </TableCell>
                <TableCell className={`text-right tabular-nums text-sm font-medium ${r.amount < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                  {fmt$(r.amount)}
                </TableCell>
                <TableCell className="text-xs">
                  {r.is_duplicate && (
                    <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 text-[10px]">
                      dup · {r.duplicate_of}
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Rules view — list, edit, enable/disable, reapply
// ────────────────────────────────────────────────────────────────────────────
function RulesView({
  rules, categories, categoryNameById, newRule, setNewRule, addRule,
  patchRule, deleteRule, reapplyAllRules,
  editingRuleId, setEditingRuleId, editingRuleDraft, setEditingRuleDraft,
}) {
  const startEdit = (r) => {
    setEditingRuleId(r.id);
    setEditingRuleDraft({ pattern: r.pattern, category: r.category, subcategory: r.subcategory || '' });
  };
  const saveEdit = async () => {
    const p = editingRuleDraft;
    await patchRule(editingRuleId, {
      pattern: p.pattern,
      category: p.category,
      subcategory: p.subcategory || null,
    });
    setEditingRuleId(null);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Your Rules ({rules.user.length})</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={reapplyAllRules}
            className="border-blue-400 text-blue-700 hover:bg-blue-50"
            data-testid="reapply-all-rules-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reapply all to existing
          </Button>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {rules.user.length === 0 && (
            <div className="text-xs text-muted-foreground italic py-3">No custom rules yet. Add one on the right to categorize future imports automatically.</div>
          )}
          {rules.user.map((r) => {
            const isEditing = editingRuleId === r.id;
            const isEnabled = r.enabled !== false;
            return (
              <div key={r.id} className={`flex items-center gap-2 border rounded p-2 text-sm ${!isEnabled ? 'opacity-50 bg-slate-50 dark:bg-slate-900/40' : ''}`} data-testid={`rule-${r.id}`}>
                <label className="flex items-center gap-1.5 shrink-0" title={isEnabled ? 'Disable rule' : 'Enable rule'}>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => patchRule(r.id, { enabled: e.target.checked })}
                    data-testid={`rule-toggle-${r.id}`}
                  />
                </label>
                {isEditing ? (
                  <>
                    <Input
                      value={editingRuleDraft.pattern}
                      onChange={(e) => setEditingRuleDraft({ ...editingRuleDraft, pattern: e.target.value })}
                      className="h-7 text-xs font-mono flex-1 min-w-0"
                      data-testid={`rule-edit-pattern-${r.id}`}
                    />
                    <Select value={editingRuleDraft.category} onValueChange={(v) => setEditingRuleDraft({ ...editingRuleDraft, category: v })}>
                      <SelectTrigger className="h-7 text-xs w-36" data-testid={`rule-edit-cat-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                    </Select>
                    <Input
                      value={editingRuleDraft.subcategory}
                      onChange={(e) => setEditingRuleDraft({ ...editingRuleDraft, subcategory: e.target.value })}
                      className="h-7 text-xs w-32"
                      placeholder="subcategory"
                      data-testid={`rule-edit-sub-${r.id}`}
                    />
                    <Button size="icon" variant="ghost" onClick={saveEdit} className="h-7 w-7" data-testid={`rule-save-${r.id}`}>
                      <Check className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditingRuleId(null)} className="h-7 w-7">
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[11px] bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 inline-block truncate max-w-full">{r.pattern}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        → <strong>{categoryNameById[r.category] || r.category}</strong>{r.subcategory ? ` / ${r.subcategory}` : ''}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => startEdit(r)} className="h-7 w-7" data-testid={`rule-edit-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteRule(r.id)} className="h-7 w-7 text-red-600" data-testid={`delete-rule-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
          <div className="text-xs text-muted-foreground pt-3 border-t mt-3">
            <strong>{rules.builtin.length}</strong> built-in rules ship with the app (fuel, insurance, ACH, Zelle, payroll, factoring, tax, bank fees, etc.). Your rules run first.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Wand2 className="h-4 w-4" /> Add a rule</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <RulePatternInput
            value={newRule.pattern}
            onChange={(pattern) => setNewRule({ ...newRule, pattern })}
            amountOp={newRule.amount_op}
            amountValue={newRule.amount_value}
            testId="new-rule-pattern"
          />
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={newRule.category} onValueChange={(v) => setNewRule({ ...newRule, category: v })}>
              <SelectTrigger data-testid="new-rule-category"><SelectValue placeholder="Pick a category…" /></SelectTrigger>
              <SelectContent>{categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Subcategory (optional)</Label>
            <Input value={newRule.subcategory} onChange={(e) => setNewRule({ ...newRule, subcategory: e.target.value })}
                   placeholder="e.g. Fuel · Pilot" data-testid="new-rule-subcategory" />
          </div>
          <div className="border rounded p-2 bg-slate-50 dark:bg-slate-900/40 space-y-2">
            <Label className="text-xs font-medium">Amount condition (optional, AND with description)</Label>
            <div className="flex gap-2 items-center">
              <Select value={newRule.amount_op || 'none'}
                      onValueChange={(v) => setNewRule({ ...newRule, amount_op: v === 'none' ? '' : v })}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="new-rule-amount-op"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No amount check</SelectItem>
                  <SelectItem value="gt">Greater than (&gt;)</SelectItem>
                  <SelectItem value="lt">Less than (&lt;)</SelectItem>
                  <SelectItem value="gte">≥ Greater or equal</SelectItem>
                  <SelectItem value="lte">≤ Less or equal</SelectItem>
                  <SelectItem value="eq">= Equals</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number" step="0.01" placeholder="0.00"
                value={newRule.amount_value}
                onChange={(e) => setNewRule({ ...newRule, amount_value: e.target.value })}
                className="h-8 text-xs w-28"
                disabled={!newRule.amount_op}
                data-testid="new-rule-amount-value"
              />
            </div>
            <label className="flex items-center gap-2 text-xs mt-1">
              <input
                type="checkbox"
                checked={newRule.exclude_on_match}
                onChange={(e) => setNewRule({ ...newRule, exclude_on_match: e.target.checked })}
                data-testid="new-rule-exclude-on-match"
              />
              Also EXCLUDE matching transactions (hidden from Money-In / Out totals)
            </label>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={newRule.apply_to_existing} onChange={(e) => setNewRule({ ...newRule, apply_to_existing: e.target.checked })}
                   data-testid="new-rule-apply-existing" />
            Also apply to existing uncategorized transactions
          </label>
          <Button
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            onClick={addRule}
            data-testid="add-rule-btn"
          >
            <PlusCircle className="h-4 w-4 mr-1.5" />Save rule
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Rule pattern input with live autocomplete of matching existing transactions.
// ────────────────────────────────────────────────────────────────────────────
function RulePatternInput({ value, onChange, amountOp, amountValue, testId }) {
  const [matches, setMatches] = useState([]);
  const [showList, setShowList] = useState(false);
  const [totals, setTotals] = useState({ count: 0, in: 0, out: 0, amount: 0 });
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setMatches([]);
      setTotals({ count: 0, in: 0, out: 0, amount: 0 });
      return;
    }
    clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { pattern: value.trim(), limit: 20 };
        if (amountOp && amountValue) {
          params.amount_op = amountOp;
          params.amount_value = parseFloat(amountValue);
        }
        const res = await bankReconApi.previewRuleMatches(params);
        setMatches(res.data.matches || []);
        setTotals({
          count: res.data.total_count || res.data.total || 0,
          in: res.data.money_in || 0,
          out: res.data.money_out || 0,
          amount: res.data.total_amount || 0,
        });
      } catch {
        setMatches([]);
        setTotals({ count: 0, in: 0, out: 0, amount: 0 });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [value, amountOp, amountValue]);

  const showBanner = value.trim().length >= 2 && !loading;

  return (
    <div className="relative">
      <Label className="text-xs">Match pattern (description contains — case-insensitive)</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowList(true)}
        onBlur={() => setTimeout(() => setShowList(false), 200)}
        placeholder="e.g. pilot|flying j"
        data-testid={testId}
      />
      <p className="text-[10px] text-muted-foreground mt-1">
        Regex OK. <code>|</code> = OR. Matching is <strong>case-insensitive</strong>: {'"'}PILOT{'"'} matches {'"'}pilot{'"'}.
      </p>
      {/* Rule preview banner — visible whenever the user has typed something */}
      {showBanner && (
        <div
          className={`mt-2 rounded border p-2 text-xs ${totals.count === 0
            ? 'border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800'
            : 'border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-800'}`}
          data-testid="rule-preview-banner"
        >
          {totals.count === 0 ? (
            <span>No existing transactions match this pattern yet.</span>
          ) : (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                Matches{' '}
                <strong data-testid="rule-preview-count">{totals.count}</strong>
                {' '}txn{totals.count === 1 ? '' : 's'}
              </span>
              {totals.in > 0 && (
                <span className="text-emerald-700 dark:text-emerald-400" data-testid="rule-preview-in">
                  +{fmt$(totals.in)} in
                </span>
              )}
              {totals.out > 0 && (
                <span className="text-red-700 dark:text-red-400" data-testid="rule-preview-out">
                  -{fmt$(totals.out)} out
                </span>
              )}
              <span className="text-slate-600 dark:text-slate-400">
                | net {' '}
                <strong className={totals.amount >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}
                        data-testid="rule-preview-amount">
                  {fmt$(totals.amount)}
                </strong>
              </span>
            </div>
          )}
        </div>
      )}
      {showList && value.trim().length >= 2 && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid="rule-autocomplete-panel"
        >
          <div className="px-2 py-1 text-[10px] text-muted-foreground border-b bg-slate-50 dark:bg-slate-800/40 flex items-center justify-between">
            <span>{loading ? 'Searching…' : `${matches.length} unique · ${totals.count} total matching txns`}</span>
            {matches.length > 0 && (
              <span className="text-[10px]">Click to fill</span>
            )}
          </div>
          {matches.length === 0 && !loading ? (
            <div className="p-3 text-xs text-muted-foreground italic text-center">No transactions match this pattern yet.</div>
          ) : (
            matches.map((m, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onChange(m.merchant || m.description); }}
                className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-blue-50 dark:hover:bg-blue-950/30 border-b last:border-0 flex items-center justify-between gap-2"
                data-testid={`rule-autocomplete-item-${i}`}
              >
                <span className="truncate flex-1">{m.merchant || m.description}</span>
                <span className={`shrink-0 tabular-nums ${(m.amount || 0) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                  {fmt$(m.amount || 0)}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inline editable label chip for a single transaction row
// ────────────────────────────────────────────────────────────────────────────
function LabelInline({ value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (value || '')) onSave(next);
  };

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        className="h-6 text-[10px] px-1.5 py-0 max-w-[160px]"
        placeholder="Label…"
        data-testid={`${testId}-input`}
      />
    );
  }

  if (value) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 px-1.5 py-0 h-5 rounded border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 text-[10px] font-medium transition-colors"
        title="Click to edit label"
        data-testid={testId}
      >
        <Tag className="h-2.5 w-2.5" />
        <span className="truncate max-w-[140px]">{value}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1 px-1.5 py-0 h-5 rounded border border-dashed border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 text-[10px] transition-colors"
      title="Add a custom label"
      data-testid={`${testId}-add`}
    >
      <Tag className="h-2.5 w-2.5" />
      + Label
    </button>
  );
}
